import type { MaybeRefOrGetter, Ref } from 'vue';
import type { FieldWildcard, PerActionFieldRulesOrWildcard, PerActionItemRule, PolicyDocumentAction } from '#layers/access';
import type { Filter, LogicalFilterAND } from '#layers/data-model';
import { cloneDeep, isEqual } from 'lodash-es';
import { computed, ref, toValue, watch } from 'vue';
import { FIELD_WILDCARD } from '#layers/access';
import { isEmptyFilter, isLogicalFilterAnd } from '#layers/data-model';

interface UsePolicyRuleChangesOptions {
	itemRules: Ref<PerActionItemRule>;
	fieldRules: Ref<PerActionFieldRulesOrWildcard>;
	action: MaybeRefOrGetter<PolicyDocumentAction>;
}

// Serves as type guard
function isWildcard(fieldRules: PerActionFieldRulesOrWildcard): fieldRules is FieldWildcard {
	return fieldRules === FIELD_WILDCARD;
}

export function usePolicyFieldRules({ itemRules: itemRulesRef, fieldRules: fieldRulesRef, action }: UsePolicyRuleChangesOptions) {
	// The need for this could be avoided by using oldValue in watchers.
	// But with `{ deep: true }`; the old value shows the same value as the new value.
	// Reference: https://vuejs.org/guide/essentials/watchers.html#deep-watchers
	const previousFieldRules = ref(cloneDeep(fieldRulesRef.value));
	const previousItemRules = ref(cloneDeep(itemRulesRef.value));

	const itemRules = computed(() => toValue(itemRulesRef));
	const fieldRules = computed(() => toValue(fieldRulesRef));

	// When item rules change, we need to update the field rules
	watch(itemRules, async () => {
		if (toValue(action) === 'create') {
			return;
		}

		if (isWildcard(fieldRules.value)) {
			previousItemRules.value = cloneDeep(itemRules.value);
			return;
		}

		fieldRulesRef.value = Object.keys(fieldRules.value).reduce((acc, key) => {
			if (toValue(action) === 'create') {
				acc[key] = {};
			}
			else if (!itemRules.value?.filter && !isWildcard(fieldRules.value) && isEqual(previousItemRules.value?.filter, fieldRules.value?.[key]?.filter)) {
				acc[key] = {};
			}
			else {
				acc[key] = { filter: createFieldRulesFilter(key) as LogicalFilterAND };
			}

			return acc;
		}, {} as Record<string, { filter?: LogicalFilterAND }>);

		previousItemRules.value = cloneDeep(itemRules.value);
	}, { deep: true });

	// When we add field rules, we need to take into consideration the item rules
	// (that's why we watch the length of the field rules)
	watch(() => Object.keys(fieldRules.value).length, () => {
		if (toValue(action) === 'create' || isWildcard(fieldRules.value)) {
			previousFieldRules.value = FIELD_WILDCARD;
			return;
		}

		for (const key of Object.keys(fieldRules.value)) {
			if (fieldRules.value[key] && (previousFieldRules.value === FIELD_WILDCARD || isEmptyFilter(previousFieldRules.value?.[key]?.filter))) {
				if (!(itemRules.value?.filter)) {
					fieldRules.value[key as keyof PerActionFieldRulesOrWildcard] = {};
				}
				else {
					const forceItemRule = isEqual(previousItemRules.value?.filter, fieldRules.value?.[key]?.filter);
					fieldRules.value[key as keyof PerActionFieldRulesOrWildcard] = { filter: createFieldRulesFilter(key, { forceItemRule }) as LogicalFilterAND };
				}
			}
		}

		previousFieldRules.value = cloneDeep(fieldRules.value);
	});

	function hasFollowItemRules(fieldName: string) {
		if (isWildcard(fieldRules.value)) {
			return false;
		}

		if (!(itemRules.value?.filter) && !(fieldRules.value?.[fieldName]?.filter)) {
			return true;
		}

		return isEqual(previousItemRules.value?.filter, fieldRules.value?.[fieldName]?.filter);
	}

	function createFieldRulesFilter(fieldName: string, options: { forceItemRule?: boolean } = {}) {
		if (isWildcard(fieldRules.value)) {
			return fieldRules.value;
		}

		const newFilter: Filter = { _and: [] };
		const isSameAsItemRuleFilter = isEqual(previousItemRules.value?.filter, fieldRules.value?.[fieldName]?.filter);

		// add item rules
		if (isSameAsItemRuleFilter || !(fieldRules.value?.[fieldName]?.filter) || options?.forceItemRule) {
			newFilter._and.push(...itemRules.value?.filter?._and ?? []);
		}
		// add original field rules
		else if (!isEmptyFilter(itemRules.value?.filter) && isLogicalFilterAnd(itemRules.value?.filter)) {
			newFilter._and.push(...fieldRules.value?.[fieldName]?.filter?._and ?? []);
		}

		return newFilter;
	}

	return {
		hasFollowItemRules,
	};
}
